import { Injectable } from '@angular/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { NavigationItem } from './navigation-item.interface';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NavigationLoaderService {
  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor(private readonly layoutService: VexLayoutService) {
    this.loadNavigation();
  }

  loadNavigation(): void {
    const raw = localStorage.getItem('menuArbol');
    let menuArbol: any[] = [];
    try {
      menuArbol = JSON.parse(raw || '[]');
    } catch {
      menuArbol = [];
    }

    if (Array.isArray(menuArbol) && menuArbol.length > 0) {
      this._items.next(this.convertMenuToNavigation(menuArbol));
    } else {
      // Sin permisos efectivos: sidenav vacío (no usar el menú hardcodeado completo).
      // getDefaultNavigation() se conserva solo como referencia de seed/documentación.
      this._items.next([]);
    }
  }

  /** Referencia del catálogo hardcodeado (no usar como fallback de sesión). */
  getSeedNavigationReference(): NavigationItem[] {
    return this.getDefaultNavigation();
  }

  private convertMenuToNavigation(menuArbol: any[]): NavigationItem[] {
    const navigationItems: NavigationItem[] = [];
    const isVisible = (value: unknown): boolean =>
      value !== false && value !== 0 && value !== '0';

    menuArbol.forEach((menuItem) => {
      if (!isVisible(menuItem?.visible)) return;

      const children = Array.isArray(menuItem.subItems) ? menuItem.subItems : [];
      const visibleChildren = children.filter((subItem: any) => isVisible(subItem?.visible));

      // Si llegó plano (sin subItems), no inventar secciones vacías
      if (visibleChildren.length === 0) {
        return;
      }

      const navigationItem: NavigationItem = {
        type: 'subheading',
        label: menuItem.label,
        children: [],
      };

      visibleChildren.forEach((subItem: any) => {
        const nested = Array.isArray(subItem.subItems)
          ? subItem.subItems.filter((n: any) => isVisible(n?.visible))
          : [];

        if (nested.length > 0) {
          nested.forEach((leaf: any) => {
            navigationItem.children.push({
              type: 'link',
              label: leaf.label,
              route: leaf.link || `/${String(leaf.label || '').toLowerCase().replace(/\s+/g, '-')}`,
              icon: this.getIconForMenu(leaf.label, leaf.link),
              routerLinkActiveOptions: { exact: false },
            });
          });
          return;
        }

        navigationItem.children.push({
          type: 'link',
          label: subItem.label,
          route: subItem.link || `/${String(subItem.label || '').toLowerCase().replace(/\s+/g, '-')}`,
          icon: this.getIconForMenu(subItem.label, subItem.link),
          routerLinkActiveOptions: { exact: false },
        });
      });

      if (navigationItem.children.length > 0) {
        navigationItems.push(navigationItem);
      }
    });

    return navigationItems;
  }

  /** Icono del sidenav para un menú (label/route). Reutilizado en IAM. */
  getIconForMenu(label?: string | number, route?: string): string {
    const byLabel: Record<string, string> = {
      'pedidos pendientes': 'mat:shopping_cart',
      'órdenes': 'mat:receipt_long',
      'ordenes': 'mat:receipt_long',
      'ventas por vendedor': 'mat:storefront',
      'pedidos por ruta': 'mat:local_shipping',
      'venta preventista': 'mat:assignment_ind',
      'consignaciones': 'mat:swap_horiz',
      'productos': 'mat:inventory',
      'atributos de productos': 'mat:tune',
      'inventarios': 'mat:warehouse',
      'definir rutas': 'mat:add_road',
      'gestión de rutas': 'mat:route',
      'gestion de rutas': 'mat:route',
      'negocios': 'mat:store',
      'movimientos entre rutas': 'mat:swap_horiz',
      'movimientos de rutas': 'mat:swap_horiz',
      'monitoreo de rutas': 'mat:timeline',
      'progreso de rutas': 'mat:timeline',
      'productividad': 'mat:pie_chart',
      'rutas de distribuidor': 'mat:local_shipping',
      'listar rutas de distribuidor': 'mat:list_alt',
      'mapa': 'mat:map',
      'vehículos': 'mat:directions_car',
      'vehiculos': 'mat:directions_car',
      'producción': 'mat:precision_manufacturing',
      'produccion': 'mat:precision_manufacturing',
      'lotes de fabricación': 'mat:view_module',
      'lotes de fabricacion': 'mat:view_module',
      'inventario de lotes': 'mat:grid_view',
      'impresión lotes': 'mat:print',
      'impresion lotes': 'mat:print',
      'costos por lote': 'mat:account_balance_wallet',
      'planeación semanal': 'mat:calendar_today',
      'planeacion semanal': 'mat:calendar_today',
      'pedido a producción': 'mat:factory',
      'pedido a produccion': 'mat:factory',
      'planeación de corte': 'mat:view_kanban',
      'planeacion de corte': 'mat:view_kanban',
      'planeación de cortes en rutas': 'mat:content_cut',
      'planeacion de cortes en rutas': 'mat:content_cut',
      'materiales de tabla': 'mat:layers',
      'piezas de tabla': 'mat:grid_on',
      'optimizer': 'mat:auto_awesome',
      'tipos de insumos': 'mat:category',
      'insumos': 'mat:inventory_2',
      'compras': 'mat:add_shopping_cart',
      'entregas': 'mat:move_to_inbox',
      'proveedores': 'mat:business',
      'reporte personal': 'mat:badge',
      'horarios de empleados': 'mat:schedule',
      'reporte de asistencias': 'mat:fact_check',
      'marcajes': 'mat:fingerprint',
      'historial por bloque': 'mat:history',
      'reporte por rango': 'mat:date_range',
      'nómina administrativa': 'mat:account_balance_wallet',
      'nomina administrativa': 'mat:account_balance_wallet',
      'nómina de ventas': 'mat:local_shipping',
      'nomina de ventas': 'mat:local_shipping',
      'transferencias': 'mat:swap_horiz',
      'chat': 'mat:chat',
      'chat ollama': 'mat:smart_toy',
      'asignaciones': 'mat:view_kanban',
      'roles': 'mat:admin_panel_settings',
      'catálogo de menús': 'mat:menu',
      'catalogo de menus': 'mat:menu',
      'iam': 'mat:security',
      'tablero': 'mat:view_kanban',
      'nuevo ticket': 'mat:assignment',
      'organigrama': 'mat:account_tree',
      'tipos por área': 'mat:category',
      'tipos por area': 'mat:category',
      'recap': 'mat:summarize',
      'calendario': 'mat:calendar_month',
      'documentos': 'mat:folder_shared',
      'ver documentos': 'mat:folder_shared',
      'tipos de documentos': 'mat:category',
      'archivo': 'mat:folder_open',
      'dashboard supervisor': 'mat:insights',
      'dashboard atm': 'mat:atm',
      'registrar gasto': 'mat:receipt',
      'tipos de gastos': 'mat:sell',
      'estado de resultados': 'mat:bar_chart',
      'estado de resultados del taller': 'mat:precision_manufacturing',
      'analítica de rutas': 'mat:insights',
      'analitica de rutas': 'mat:insights',
    };

    const byRoute: Record<string, string> = {
      '/administrativo/pedidos-pendientes': 'mat:shopping_cart',
      '/administrativo/ordenes': 'mat:receipt_long',
      '/administrativo/preventas-vendedor': 'mat:storefront',
      '/administrativo/pedidos-ruta': 'mat:local_shipping',
      '/administrativo/venta-preventista': 'mat:assignment_ind',
      '/administrativo/consignaciones': 'mat:swap_horiz',
      '/administrativo/productos': 'mat:inventory',
      '/administrativo/atributos-productos': 'mat:tune',
      '/administrativo/inventarios': 'mat:warehouse',
      '/administrativo/definicion-rutas': 'mat:add_road',
      '/administrativo/gestion-rutas': 'mat:route',
      '/administrativo/negocios': 'mat:store',
      '/administrativo/movimientos-rutas': 'mat:swap_horiz',
      '/administrativo/progreso-rutas': 'mat:timeline',
      '/administrativo/productividad': 'mat:pie_chart',
      '/administrativo/rutas-distribuidor': 'mat:local_shipping',
      '/administrativo/listar-rutas-distribuidor': 'mat:list_alt',
      '/administrativo/mapa': 'mat:map',
      '/administrativo/vehiculos': 'mat:directions_car',
      '/administrativo/produccion': 'mat:precision_manufacturing',
      '/administrativo/lotes-fabricacion': 'mat:view_module',
      '/administrativo/lotes-fabricacion/inventario': 'mat:grid_view',
      '/administrativo/costos-lotes': 'mat:account_balance_wallet',
      '/administrativo/planeacion-semanal': 'mat:calendar_today',
      '/administrativo/pedido-produccion': 'mat:factory',
      '/administrativo/planeacion-corte': 'mat:view_kanban',
      '/administrativo/planeacion-cortes-rutas': 'mat:content_cut',
      '/administrativo/table-materials': 'mat:layers',
      '/administrativo/table-pieces': 'mat:grid_on',
      '/administrativo/optimizer': 'mat:auto_awesome',
      '/administrativo/supplies/types': 'mat:category',
      '/administrativo/supplies': 'mat:inventory_2',
      '/administrativo/supplies/purchases': 'mat:add_shopping_cart',
      '/administrativo/supplies/deliveries': 'mat:move_to_inbox',
      '/administrativo/supplies/providers': 'mat:business',
      '/personal/reporte-personal': 'mat:badge',
      '/employee-schedules': 'mat:schedule',
      '/employee-schedules/reporte-asistencias': 'mat:fact_check',
      '/employee-schedules/marcajes': 'mat:fingerprint',
      '/employee-schedules/historial-bloques': 'mat:history',
      '/employee-schedules/reporte-por-rango': 'mat:date_range',
      '/administrativo/payroll': 'mat:account_balance_wallet',
      '/administrativo/payroll-ventas': 'mat:local_shipping',
      '/administrativo/transferencias': 'mat:swap_horiz',
      '/chat': 'mat:chat',
      '/asignaciones': 'mat:view_kanban',
      '/asignaciones/nuevo': 'mat:assignment',
      '/asignaciones/organigrama': 'mat:account_tree',
      '/asignaciones/tipos': 'mat:category',
      '/asignaciones/recap': 'mat:summarize',
      '/calendario': 'mat:calendar_month',
      '/documentos': 'mat:folder_shared',
      '/documentos/tipos': 'mat:category',
      '/iam/roles': 'mat:admin_panel_settings',
      '/iam/menus': 'mat:menu',
      '/administrativo/dashboard-supervisor': 'mat:insights',
      '/administrativo/atm-dashboard': 'mat:atm',
      '/administrativo/gastos': 'mat:receipt',
      '/administrativo/tipos-gastos': 'mat:sell',
      '/administrativo/estado-resultados': 'mat:bar_chart',
      '/administrativo/taller': 'mat:precision_manufacturing',
    };

    const normalizedLabel = String(label ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const normalizedRoute = String(route ?? '').trim().replace(/\/+$/, '');

    if (normalizedRoute && byRoute[normalizedRoute]) {
      return byRoute[normalizedRoute];
    }

    // Lookup sin acentos + con acentos (mapa ya normalizado parcialmente)
    for (const [key, icon] of Object.entries(byLabel)) {
      const keyNorm = key.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (keyNorm === normalizedLabel || key === String(label ?? '').trim().toLowerCase()) {
        return icon;
      }
    }

    return 'mat:folder';
  }

  private getDefaultNavigation(): NavigationItem[] {
    return [
      // manera de seleccionar  los lotes de produccion semanal , para guardarlo (pa donde lo mandamos )
      // cambiar mapa preventista para ponderar de manera diferente los productos
      {
        type: 'subheading',
        label: 'Dashboards',
        children: [
          {
            type: 'link',
            label: 'Dashboard Supervisor',
            route: '/administrativo/dashboard-supervisor',
            icon: 'mat:insights',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Cajero',
            route: '/administrativo/atm-dashboard',
            icon: 'mat:atm',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Ventas',
        children: [
          {
            type: 'link',
            label: 'Pedidos Pendientes',
            route: '/administrativo/pedidos-pendientes',
            icon: 'mat:shopping_cart',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Órdenes',
            route: '/administrativo/ordenes',
            icon: 'mat:receipt_long',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Ventas por Vendedor',
            route: '/administrativo/preventas-vendedor',
            icon: 'mat:storefront',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Pedidos por Ruta',
            route: '/administrativo/pedidos-ruta',
            icon: 'mat:local_shipping',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Venta Preventista',
            route: '/administrativo/venta-preventista',
            icon: 'mat:assignment_ind',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Consignaciones',
            route: '/administrativo/consignaciones',
            icon: 'mat:swap_horiz',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Catálogo',
        children: [
          {
            type: 'link',
            label: 'Productos',
            route: '/administrativo/productos',
            icon: 'mat:inventory',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Atributos de Productos',
            route: '/administrativo/atributos-productos',
            icon: 'mat:tune',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Inventarios',
            route: '/administrativo/inventarios',
            icon: 'mat:warehouse',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Distribución',
        children: [
          {
            type: 'dropdown',
            label: 'Rutas',
            icon: 'mat:route',
            children: [
              {
                type: 'link',
                label: 'Definir Rutas',
                route: '/administrativo/definicion-rutas',
                icon: 'mat:add_road',
                routerLinkActiveOptions: { exact: false }
              },
              {
                type: 'link',
                label: 'Gestión de Rutas',
                route: '/administrativo/gestion-rutas',
                icon: 'mat:route',
                routerLinkActiveOptions: { exact: false }
              },
              {
                type: 'link',
                label: 'Movimientos entre Rutas',
                route: '/administrativo/movimientos-rutas',
                icon: 'mat:swap_horiz',
                routerLinkActiveOptions: { exact: false }
              },
              {
                type: 'link',
                label: 'Rutas de Distribuidor',
                route: '/administrativo/rutas-distribuidor',
                icon: 'mat:local_shipping',
                routerLinkActiveOptions: { exact: false }
              },
              {
                type: 'link',
                label: 'Listar Rutas de Distribuidor',
                route: '/administrativo/listar-rutas-distribuidor',
                icon: 'mat:list_alt',
                routerLinkActiveOptions: { exact: false }
              }
            ]
          },
          {
            type: 'link',
            label: 'Negocios',
            route: '/administrativo/negocios',
            icon: 'mat:store',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Monitoreo de Rutas',
            route: '/administrativo/progreso-rutas',
            icon: 'mat:timeline',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Productividad',
            route: '/administrativo/productividad',
            icon: 'mat:pie_chart',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Mapa',
            route: '/administrativo/mapa',
            icon: 'mat:map',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Vehículos',
            route: '/administrativo/vehiculos',
            icon: 'mat:directions_car',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Producción',
        children: [
          {
            type: 'link',
            label: 'Producción',
            route: '/administrativo/produccion',
            icon: 'mat:precision_manufacturing',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Lotes de Fabricación',
            route: '/administrativo/lotes-fabricacion',
            icon: 'mat:view_module',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Inventario de Lotes',
            route: '/administrativo/lotes-fabricacion/inventario',
            icon: 'mat:grid_view',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Impresión Lotes',
            route: '/administrativo/impresion-lotes',
            icon: 'mat:print',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Costos por Lote',
            route: '/administrativo/costos-lotes',
            icon: 'mat:account_balance_wallet',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'dropdown',
            label: 'Planeación',
            icon: 'mat:event_note',
            children: [
              {
                type: 'link',
                label: 'Planeación Semanal',
                route: '/administrativo/planeacion-semanal',
                icon: 'mat:calendar_today',
                routerLinkActiveOptions: { exact: false }
              },
              {
                type: 'link',
                label: 'Pedido a Producción',
                route: '/administrativo/pedido-produccion',
                icon: 'mat:factory',
                routerLinkActiveOptions: { exact: false }
              },
              {
                type: 'link',
                label: 'Planeación de Corte',
                route: '/administrativo/planeacion-corte',
                icon: 'mat:view_kanban',
                routerLinkActiveOptions: { exact: false }
              },
              {
                type: 'link',
                label: 'Planeación de Cortes en Rutas',
                route: '/administrativo/planeacion-cortes-rutas',
                icon: 'mat:content_cut',
                routerLinkActiveOptions: { exact: false }
              }
            ]
          },
          {
            type: 'link',
            label: 'Materiales de Tabla',
            route: '/administrativo/table-materials',
            icon: 'mat:layers',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Piezas de Tabla',
            route: '/administrativo/table-pieces',
            icon: 'mat:grid_on',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Optimizer',
            route: '/administrativo/optimizer',
            icon: 'mat:auto_awesome',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Insumos',
        children: [
          {
            type: 'link',
            label: 'Tipos de Insumos',
            route: '/administrativo/supplies/types',
            icon: 'mat:category',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Insumos',
            route: '/administrativo/supplies',
            icon: 'mat:inventory_2',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Compras',
            route: '/administrativo/supplies/purchases',
            icon: 'mat:add_shopping_cart',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Entregas',
            route: '/administrativo/supplies/deliveries',
            icon: 'mat:move_to_inbox',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Proveedores',
            route: '/administrativo/supplies/providers',
            icon: 'mat:business',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Personal',
        children: [
          {
            type: 'link',
            label: 'Horarios de Empleados',
            route: '/employee-schedules',
            icon: 'mat:schedule',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Marcajes',
            route: '/employee-schedules/marcajes',
            icon: 'mat:fingerprint',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Nómina Administrativa',
            route: '/administrativo/payroll',
            icon: 'mat:account_balance_wallet',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Nómina de Ventas',
            route: '/administrativo/payroll-ventas',
            icon: 'mat:local_shipping',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'dropdown',
            label: 'Reportes',
            icon: 'mat:summarize',
            children: [
              {
                type: 'link',
                label: 'Reporte Personal',
                route: '/personal/reporte-personal',
                icon: 'mat:badge',
                routerLinkActiveOptions: { exact: false }
              },
              {
                type: 'link',
                label: 'Reporte de Asistencias',
                route: '/employee-schedules/reporte-asistencias',
                icon: 'mat:fact_check',
                routerLinkActiveOptions: { exact: true }
              },
              {
                type: 'link',
                label: 'Historial por Bloque',
                route: '/employee-schedules/historial-bloques',
                icon: 'mat:history',
                routerLinkActiveOptions: { exact: true }
              },
              {
                type: 'link',
                label: 'Reporte por Rango',
                route: '/employee-schedules/reporte-por-rango',
                icon: 'mat:date_range',
                routerLinkActiveOptions: { exact: true }
              }
            ]
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Gastos',
        children: [
          {
            type: 'link',
            label: 'Registrar Gasto',
            route: '/administrativo/gastos',
            icon: 'mat:receipt',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Tipos de Gastos',
            route: '/administrativo/tipos-gastos',
            icon: 'mat:sell',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Contabilidad',
        children: [
          {
            type: 'link',
            label: 'Transferencias',
            route: '/administrativo/transferencias',
            icon: 'mat:swap_horiz',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Estado de Resultados',
            route: '/administrativo/estado-resultados',
            icon: 'mat:bar_chart',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Historial de Estados de Resultados',
            route: '/administrativo/estado-resultados-historial',
            icon: 'mat:history',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Estado de Resultados del Taller',
            route: '/administrativo/taller',
            icon: 'mat:precision_manufacturing',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Analítica de rutas',
            route: '/administrativo/analitica-rutas',
            icon: 'mat:insights',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Comunicación',
        children: [
          {
            type: 'link',
            label: 'Chat',
            route: '/chat',
            icon: 'mat:chat',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Asignaciones',
        children: [
          {
            type: 'link',
            label: 'Tablero',
            route: '/asignaciones',
            icon: 'mat:view_kanban',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Nuevo Ticket',
            route: '/asignaciones/nuevo',
            icon: 'mat:assignment',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Organigrama',
            route: '/asignaciones/organigrama',
            icon: 'mat:account_tree',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Tipos por área',
            route: '/asignaciones/tipos',
            icon: 'mat:category',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Recap',
            route: '/asignaciones/recap',
            icon: 'mat:summarize',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Calendario',
            route: '/calendario',
            icon: 'mat:calendar_month',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Documentos',
        children: [
          {
            type: 'link',
            label: 'Ver documentos',
            route: '/documentos',
            icon: 'mat:folder_shared',
            routerLinkActiveOptions: { exact: true }
          },
          {
            type: 'link',
            label: 'Tipos de documentos',
            route: '/documentos/tipos',
            icon: 'mat:category',
            routerLinkActiveOptions: { exact: true }
          }
        ]
      },
      {
        type: 'subheading',
        label: 'IAM',
        children: [
          {
            type: 'link',
            label: 'Roles',
            route: '/iam/roles',
            icon: 'mat:admin_panel_settings',
            routerLinkActiveOptions: { exact: false }
          },
          {
            type: 'link',
            label: 'Catálogo de menús',
            route: '/iam/menus',
            icon: 'mat:menu',
            routerLinkActiveOptions: { exact: false }
          }
        ]
      }
    ];
  }

  // Método para recargar el menú (útil después del login)
  reloadNavigation(): void {
    this.loadNavigation();
  }
}
